import type { AgentSnapshotPayload } from "../messages.js";
import type { SerializableAgentConfig, StoredAgentRecord } from "./agent-storage.js";
import type {
  AgentCapabilityFlags,
  AgentFeature,
  AgentMetadata,
  AgentMode,
  AgentPermissionRequest,
  AgentPersistenceHandle,
  AgentRateLimit,
  AgentSessionConfig,
  AgentRuntimeInfo,
  AgentUsage,
  McpServerConfig,
  RateLimitType,
} from "./agent-sdk-types.js";
import type { ManagedAgent } from "./agent-manager.js";
import type { JsonValue } from "../json-utils.js";
import type { Logger } from "pino";
import { buildProviderRegistry } from "./provider-registry.js";
import { coerceAgentProvider, toAgentPersistenceHandle } from "../persistence-hooks.js";

export type { ManagedAgent };

type ProjectionOptions = {
  title?: string | null;
  createdAt?: string;
  internal?: boolean;
};

function normalizeThinkingOptionId(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

export function resolveEffectiveThinkingOptionId(options: {
  runtimeInfo?: AgentRuntimeInfo | null;
  configuredThinkingOptionId?: string | null;
}): string | null {
  const runtimeInfo = options.runtimeInfo;
  if (runtimeInfo && "thinkingOptionId" in runtimeInfo) {
    return normalizeThinkingOptionId(runtimeInfo.thinkingOptionId);
  }
  return normalizeThinkingOptionId(options.configuredThinkingOptionId);
}

export function toStoredAgentRecord(
  agent: ManagedAgent,
  options?: ProjectionOptions,
): StoredAgentRecord {
  const createdAt = options?.createdAt ?? agent.createdAt.toISOString();
  const config = buildSerializableConfig(agent.config);
  const persistence = sanitizePersistenceHandle(agent.persistence);
  const runtimeInfo = sanitizeRuntimeInfo(agent.runtimeInfo);

  return {
    id: agent.id,
    provider: agent.provider,
    cwd: agent.cwd,
    createdAt,
    updatedAt: agent.updatedAt.toISOString(),
    lastActivityAt: agent.updatedAt.toISOString(),
    lastUserMessageAt: agent.lastUserMessageAt ? agent.lastUserMessageAt.toISOString() : null,
    lastAssistantMessageAt: agent.lastAssistantMessageAt
      ? agent.lastAssistantMessageAt.toISOString()
      : null,
    title: options?.title ?? null,
    labels: agent.labels,
    lastStatus: agent.lifecycle,
    lastModeId: agent.currentModeId ?? config?.modeId ?? null,
    config: config ?? null,
    runtimeInfo,
    features: normalizeFeatures(agent.features),
    persistence,
    lastError: agent.lastError ?? undefined,
    requiresAttention: agent.attention.requiresAttention,
    attentionReason: agent.attention.requiresAttention ? agent.attention.attentionReason : null,
    attentionTimestamp: agent.attention.requiresAttention
      ? agent.attention.attentionTimestamp.toISOString()
      : null,
    internal: options?.internal,
    // Round-trip fork lineage so a persist-then-resume cycle preserves it.
    ...(agent.parentAgentId ? { parentAgentId: agent.parentAgentId } : {}),
    ...(agent.forkedFromMessageUuid ? { forkedFromMessageUuid: agent.forkedFromMessageUuid } : {}),
    // Multi-tenant: persist the owner so daemon restart doesn't silently
    // drop session isolation. `ownerUserId` round-trips as `null`/undefined
    // for pre-Phase-2c records — they re-hydrate as unscoped.
    ownerUserId: agent.ownerUserId,
    // Persisted display name so resume can route to the right Claude
    // profile dir. Omitted from disk when null (cleaner JSON).
    ...(agent.ownerUsername ? { ownerUsername: agent.ownerUsername } : {}),
    // stackId deliberately omitted — Phase 7: stack placement lives on
    // RemoteTab.stackId (auth-server), not on the agent record.
    // Session shares — omit when empty (not present in legacy records).
    ...(agent.sessionShares?.length ? { sessionShares: agent.sessionShares } : {}),
  } satisfies StoredAgentRecord;
}

export function toAgentPayload(
  agent: ManagedAgent,
  options?: ProjectionOptions,
): AgentSnapshotPayload {
  const runtimeInfo = sanitizeRuntimeInfo(agent.runtimeInfo);
  const thinkingOptionId = agent.config.thinkingOptionId ?? null;
  const effectiveThinkingOptionId = resolveEffectiveThinkingOptionId({
    runtimeInfo,
    configuredThinkingOptionId: thinkingOptionId,
  });

  const payload: AgentSnapshotPayload = {
    id: agent.id,
    provider: agent.provider,
    cwd: agent.cwd,
    model: agent.config.model ?? null,
    thinkingOptionId,
    effectiveThinkingOptionId,
    ...(runtimeInfo ? { runtimeInfo } : {}),
    createdAt: agent.createdAt.toISOString(),
    updatedAt: agent.updatedAt.toISOString(),
    lastUserMessageAt: agent.lastUserMessageAt ? agent.lastUserMessageAt.toISOString() : null,
    lastAssistantMessageAt: agent.lastAssistantMessageAt
      ? agent.lastAssistantMessageAt.toISOString()
      : null,
    status: agent.lifecycle,
    capabilities: cloneCapabilities(agent.capabilities),
    currentModeId: agent.currentModeId,
    availableModes: cloneAvailableModes(agent.availableModes),
    features: normalizeFeatures(agent.features),
    pendingPermissions: sanitizePendingPermissions(agent.pendingPermissions),
    persistence: sanitizePersistenceHandle(agent.persistence),
    title: options?.title ?? null,
    labels: agent.labels,
    internal: agent.internal,
    // Forward archivedAt on the live broadcast so paired clients (e.g. mobile)
    // see the archive land — without this, only the device that ran the
    // archive mutation knows about it.
    archivedAt: agent.archivedAt ?? null,
    // Surface ownership so the client can render an owner badge.
    ownerUserId: agent.ownerUserId,
    // Fork lineage — the client's tab descriptor uses `parentAgentId` to
    // mark the agent as a fork (Split glyph). Carry it from the live
    // ManagedAgent so the marker doesn't disappear once the agent is
    // loaded into memory after the initial-from-storage view.
    ...(agent.parentAgentId ? { parentAgentId: agent.parentAgentId } : {}),
    ...(agent.forkedFromMessageUuid ? { forkedFromMessageUuid: agent.forkedFromMessageUuid } : {}),
    // stackId deliberately omitted from snapshot — Phase 7: stack placement
    // lives on RemoteTab.stackId (auth-server).
    // Session shares — omitted when empty so wire stays clean.
    ...(agent.sessionShares?.length ? { sessionShares: agent.sessionShares } : {}),
  };

  const usage = sanitizeUsage(agent.lastUsage);
  if (usage !== undefined) {
    payload.lastUsage = usage;
  }

  if (agent.lastError !== undefined) {
    payload.lastError = agent.lastError;
  }

  // Handle attention state
  payload.requiresAttention = agent.attention.requiresAttention;
  if (agent.attention.requiresAttention) {
    payload.attentionReason = agent.attention.attentionReason;
    payload.attentionTimestamp = agent.attention.attentionTimestamp.toISOString();
  } else {
    payload.attentionReason = null;
    payload.attentionTimestamp = null;
  }

  return payload;
}

export function buildStoredAgentPayload(
  record: StoredAgentRecord,
  providerRegistry: ReturnType<typeof buildProviderRegistry>,
  logger: Logger,
): AgentSnapshotPayload {
  const defaultCapabilities = {
    supportsStreaming: false,
    supportsSessionPersistence: true,
    supportsDynamicModes: false,
    supportsMcpServers: false,
    supportsReasoningStream: false,
    supportsToolInvocations: true,
  } as const;

  const createdAt = new Date(record.createdAt);
  const updatedAt = new Date(resolveStoredAgentPayloadUpdatedAt(record));
  const lastUserMessageAt = record.lastUserMessageAt ? new Date(record.lastUserMessageAt) : null;
  const lastAssistantMessageAt = record.lastAssistantMessageAt
    ? new Date(record.lastAssistantMessageAt)
    : null;

  const provider = coerceAgentProvider(logger, providerRegistry, record.provider, record.id);
  const runtimeInfo = record.runtimeInfo
    ? {
        provider: coerceAgentProvider(
          logger,
          providerRegistry,
          record.runtimeInfo.provider,
          record.id,
        ),
        sessionId: record.runtimeInfo.sessionId,
        ...(Object.prototype.hasOwnProperty.call(record.runtimeInfo, "model")
          ? { model: record.runtimeInfo.model ?? null }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(record.runtimeInfo, "thinkingOptionId")
          ? { thinkingOptionId: record.runtimeInfo.thinkingOptionId ?? null }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(record.runtimeInfo, "modeId")
          ? { modeId: record.runtimeInfo.modeId ?? null }
          : {}),
        ...(record.runtimeInfo.extra ? { extra: record.runtimeInfo.extra } : {}),
      }
    : undefined;

  return {
    id: record.id,
    provider,
    cwd: record.cwd,
    model: record.config?.model ?? null,
    thinkingOptionId: record.config?.thinkingOptionId ?? null,
    effectiveThinkingOptionId: resolveEffectiveThinkingOptionId({
      runtimeInfo,
      configuredThinkingOptionId: record.config?.thinkingOptionId ?? null,
    }),
    ...(runtimeInfo ? { runtimeInfo } : {}),
    createdAt: createdAt.toISOString(),
    updatedAt: updatedAt.toISOString(),
    lastUserMessageAt: lastUserMessageAt ? lastUserMessageAt.toISOString() : null,
    lastAssistantMessageAt: lastAssistantMessageAt ? lastAssistantMessageAt.toISOString() : null,
    status: record.lastStatus,
    capabilities: defaultCapabilities,
    currentModeId: record.lastModeId ?? null,
    availableModes: [],
    pendingPermissions: [],
    persistence: toAgentPersistenceHandle(logger, providerRegistry, record.persistence),
    title: record.title ?? record.config?.title ?? null,
    requiresAttention: record.requiresAttention ?? false,
    attentionReason: record.attentionReason ?? null,
    attentionTimestamp: record.attentionTimestamp ?? null,
    archivedAt: record.archivedAt ?? null,
    labels: record.labels,
    internal: record.internal,
    // Surface ownership so the client can gate owner-only actions (e.g. Share).
    ownerUserId: record.ownerUserId ?? null,
    // Fork lineage — the client's tab descriptor uses `parentAgentId` to
    // mark forked agents with the Split glyph. Without this, every freshly
    // forked tab silently loses its "I'm a fork" indicator.
    ...(record.parentAgentId ? { parentAgentId: record.parentAgentId } : {}),
    ...(record.forkedFromMessageUuid
      ? { forkedFromMessageUuid: record.forkedFromMessageUuid }
      : {}),
    // stackId deliberately omitted — Phase 7: stack placement lives on
    // RemoteTab.stackId (auth-server).
    // Session shares — omitted when empty so wire stays clean.
    ...(record.sessionShares?.length ? { sessionShares: record.sessionShares } : {}),
  };
}

export function resolveStoredAgentPayloadUpdatedAt(record: StoredAgentRecord): string {
  const timestamps = [record.updatedAt, record.lastActivityAt]
    .filter((value): value is string => typeof value === "string" && value.length > 0)
    .map((value) => ({
      raw: value,
      parsed: Date.parse(value),
    }))
    .filter((value) => !Number.isNaN(value.parsed));

  if (timestamps.length === 0) {
    return record.updatedAt;
  }

  timestamps.sort((a, b) => b.parsed - a.parsed);
  return timestamps[0].raw;
}

function buildSerializableConfig(config: AgentSessionConfig): SerializableAgentConfig | null {
  const serializable: SerializableAgentConfig = {};
  if (Object.prototype.hasOwnProperty.call(config, "title")) {
    serializable.title = config.title ?? null;
  }
  if (config.modeId) {
    serializable.modeId = config.modeId;
  }
  if (config.model) {
    serializable.model = config.model;
  }
  if (config.thinkingOptionId) {
    serializable.thinkingOptionId = config.thinkingOptionId;
  }
  if (Object.prototype.hasOwnProperty.call(config, "featureValues")) {
    const featureValues = sanitizeMetadata(config.featureValues);
    if (featureValues !== undefined) {
      serializable.featureValues = featureValues;
    }
  }
  const extra = sanitizeMetadata(config.extra);
  if (extra !== undefined) {
    serializable.extra = extra;
  }
  if (config.systemPrompt) {
    serializable.systemPrompt = config.systemPrompt;
  }
  if (config.mcpServers) {
    // Strip in-process SDK MCP servers — they hold a live `McpServer`
    // instance whose internals JSON.stringify cannot walk (recursion blows
    // the stack). They are runtime-only by nature: the Claude SDK accepts
    // them at spawn time but they cannot be reloaded from disk anyway.
    // Persisting the other variants (stdio / http / sse) is still useful so
    // the agent reloads with the same external tools.
    const persistable: Record<string, McpServerConfig> = {};
    for (const [name, server] of Object.entries(config.mcpServers)) {
      if (server.type !== "sdk") persistable[name] = server;
    }
    if (Object.keys(persistable).length > 0) {
      serializable.mcpServers = persistable;
    }
  }
  return Object.keys(serializable).length ? serializable : null;
}

function sanitizePendingPermissions(
  pending: Map<string, AgentPermissionRequest>,
): AgentPermissionRequest[] {
  return Array.from(pending.values()).map((request) => ({
    ...request,
    input: sanitizeMetadata(request.input),
    suggestions: sanitizeMetadataArray(request.suggestions),
    actions: request.actions?.map((action) => ({ ...action })),
    metadata: sanitizeMetadata(request.metadata),
  }));
}

function sanitizePersistenceHandle(
  handle: AgentPersistenceHandle | null,
): AgentPersistenceHandle | null {
  if (!handle) {
    return null;
  }
  const sanitized: AgentPersistenceHandle = {
    provider: handle.provider,
    sessionId: handle.sessionId,
  };
  if (handle.nativeHandle !== undefined) {
    sanitized.nativeHandle = handle.nativeHandle;
  }
  const metadata = sanitizeMetadata(handle.metadata);
  if (metadata !== undefined) {
    sanitized.metadata = metadata;
  }
  return sanitized;
}

function cloneCapabilities(capabilities: AgentCapabilityFlags): AgentCapabilityFlags {
  return { ...capabilities };
}

function cloneAvailableModes(modes: AgentMode[]): AgentMode[] {
  return modes.map((mode) => ({ ...mode }));
}

function normalizeFeatures(features: AgentFeature[] | null | undefined): AgentFeature[] {
  return Array.isArray(features) ? features.map((feature) => ({ ...feature })) : [];
}

/**
 * Defensive cap. Real-world agent metadata never approaches anything close
 * to this; if we hit it, something has handed us a pathological structure
 * and we'd rather drop it than crash the daemon.
 */
const SANITIZE_MAX_DEPTH = 32;

function sanitizeOptionalJson(
  value: unknown,
  seen: WeakSet<object> = new WeakSet(),
  depth = 0,
): JsonValue | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  // For object-ish values, guard against circular references and runaway
  // depth before recursing. A runtime config that holds back-refs (live SDK
  // sessions, MCP server instances, EventEmitters) would otherwise blow the
  // call stack here — the original cause of the persisted-config /
  // fetch_agent stack overflow we hit on orchestrate quests.
  if (typeof value !== "object") {
    return undefined;
  }
  if (depth >= SANITIZE_MAX_DEPTH) {
    return undefined;
  }
  if (seen.has(value as object)) {
    return undefined;
  }
  seen.add(value as object);

  if (Array.isArray(value)) {
    const sanitized = value
      .map((item) => sanitizeOptionalJson(item, seen, depth + 1))
      .filter((item) => item !== undefined);
    return sanitized;
  }
  const result: { [key: string]: JsonValue } = {};
  for (const [key, val] of Object.entries(value)) {
    const sanitized = sanitizeOptionalJson(val, seen, depth + 1);
    if (sanitized !== undefined) {
      result[key] = sanitized;
    }
  }
  return Object.keys(result).length ? result : undefined;
}

function isJsonObject(value: JsonValue): value is { [key: string]: JsonValue } {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function sanitizeMetadata(value: unknown): AgentMetadata | undefined {
  const sanitized = sanitizeOptionalJson(value);
  if (!sanitized || !isJsonObject(sanitized)) {
    return undefined;
  }
  return sanitized;
}

function sanitizeMetadataArray(value: unknown): AgentMetadata[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const sanitized = value
    .map((entry) => sanitizeMetadata(entry))
    .filter((entry): entry is AgentMetadata => entry !== undefined);
  return sanitized.length > 0 ? sanitized : undefined;
}

function sanitizeUsage(value: unknown): AgentUsage | undefined {
  const sanitized = sanitizeOptionalJson(value);
  if (!sanitized || !isJsonObject(sanitized)) {
    return undefined;
  }
  const result: AgentUsage = {};
  const inputTokens = sanitized.inputTokens;
  if (typeof inputTokens === "number" && Number.isFinite(inputTokens)) {
    result.inputTokens = inputTokens;
  } else if (inputTokens !== undefined && inputTokens !== null) {
    return undefined;
  }
  const cachedInputTokens = sanitized.cachedInputTokens;
  if (typeof cachedInputTokens === "number" && Number.isFinite(cachedInputTokens)) {
    result.cachedInputTokens = cachedInputTokens;
  } else if (cachedInputTokens !== undefined && cachedInputTokens !== null) {
    return undefined;
  }
  const outputTokens = sanitized.outputTokens;
  if (typeof outputTokens === "number" && Number.isFinite(outputTokens)) {
    result.outputTokens = outputTokens;
  } else if (outputTokens !== undefined && outputTokens !== null) {
    return undefined;
  }
  const totalCostUsd = sanitized.totalCostUsd;
  if (typeof totalCostUsd === "number" && Number.isFinite(totalCostUsd)) {
    result.totalCostUsd = totalCostUsd;
  } else if (totalCostUsd !== undefined && totalCostUsd !== null) {
    return undefined;
  }
  const contextWindowMaxTokens = sanitized.contextWindowMaxTokens;
  if (typeof contextWindowMaxTokens === "number" && Number.isFinite(contextWindowMaxTokens)) {
    result.contextWindowMaxTokens = contextWindowMaxTokens;
  } else if (contextWindowMaxTokens !== undefined && contextWindowMaxTokens !== null) {
    return undefined;
  }
  const contextWindowUsedTokens = sanitized.contextWindowUsedTokens;
  if (typeof contextWindowUsedTokens === "number" && Number.isFinite(contextWindowUsedTokens)) {
    result.contextWindowUsedTokens = contextWindowUsedTokens;
  } else if (contextWindowUsedTokens !== undefined && contextWindowUsedTokens !== null) {
    return undefined;
  }
  const rateLimits = sanitized.rateLimits;
  if (Array.isArray(rateLimits)) {
    const VALID_RATE_LIMIT_TYPES = new Set<string>([
      "five_hour",
      "seven_day",
      "seven_day_opus",
      "seven_day_sonnet",
      "overage",
    ]);
    const sanitizedRateLimits: AgentRateLimit[] = [];
    for (const entry of rateLimits) {
      if (!entry || typeof entry !== "object") continue;
      const { rateLimitType, utilization, resetsAt } = entry as Record<string, unknown>;
      if (typeof rateLimitType !== "string" || !VALID_RATE_LIMIT_TYPES.has(rateLimitType)) continue;
      if (typeof utilization !== "number" || !Number.isFinite(utilization)) continue;
      const limit: AgentRateLimit = {
        rateLimitType: rateLimitType as RateLimitType,
        utilization,
      };
      if (typeof resetsAt === "number" && Number.isFinite(resetsAt)) {
        limit.resetsAt = resetsAt;
      }
      sanitizedRateLimits.push(limit);
    }
    if (sanitizedRateLimits.length > 0) {
      result.rateLimits = sanitizedRateLimits;
    }
  }
  return Object.keys(result).length ? result : undefined;
}

function sanitizeRuntimeInfo(
  runtimeInfo: AgentRuntimeInfo | undefined,
): AgentRuntimeInfo | undefined {
  if (!runtimeInfo) {
    return undefined;
  }
  const sanitized: AgentRuntimeInfo = {
    provider: runtimeInfo.provider,
    sessionId: runtimeInfo.sessionId,
  };
  if (runtimeInfo.model !== undefined) {
    sanitized.model = runtimeInfo.model;
  }
  if (runtimeInfo.thinkingOptionId !== undefined) {
    sanitized.thinkingOptionId = runtimeInfo.thinkingOptionId;
  }
  if (runtimeInfo.modeId !== undefined) {
    sanitized.modeId = runtimeInfo.modeId;
  }
  const extra = sanitizeMetadata(runtimeInfo.extra);
  if (extra !== undefined) {
    sanitized.extra = extra;
  }
  return sanitized;
}
